import { logger } from '@magicschool/logger';
import type { CreateStudentToolTemplate } from '@magicschool/supabase/types';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ImmutableList, ImmutableMap } from 'util/immutable';
import type { ToolTemplatesResponse } from '@/app/api/tools/student/tool-templates/route';
import type { ToolCustomization } from '@/features/rooms/types';

export const ToolTemplatesQuery = queryOptions({
  queryKey: ['tool-customizations', 'templates'],
  queryFn: async (): Promise<ToolCustomization[]> => {
    const response = await fetch('/api/tools/student/tool-templates');
    if (!response.ok) throw new Error('Failed to fetch tool customization templates');
    const data = (await response.json()) as ToolTemplatesResponse;
    return data.tool_customizations;
  },
});

export type SaveToolTemplateInput = CreateStudentToolTemplate;

/** Server hardcodes tool_application: room — omit it from the client body. */
export async function saveToolTemplate(input: SaveToolTemplateInput): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/tools/student/tool-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch (error) {
    logger.error('saveToolTemplate: network error', { error });
    throw error;
  }
  if (!response.ok) {
    logger.error('saveToolTemplate: non-OK response', { status: response.status });
    throw new Error('Failed to save tool template');
  }
}

export async function deleteToolTemplate(id: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`/api/tool_customizations/${id}`, { method: 'DELETE' });
  } catch (error) {
    logger.error('deleteToolTemplate: network error', { error });
    throw error;
  }
  if (!response.ok) {
    logger.error('deleteToolTemplate: non-OK response', { status: response.status });
    throw new Error('Failed to delete tool template');
  }
}

export function useToolTemplates() {
  const { data, ...query } = useQuery(ToolTemplatesQuery);

  const toolCustomizationsMap = useMemo(() => {
    if (!data) return ImmutableMap<string, ImmutableList<ToolCustomization>>();

    const grouped = data.reduce(
      (acc, customization) => {
        const toolId = customization.tool_uuid;
        if (!acc[toolId]) {
          acc[toolId] = [];
        }
        acc[toolId].push(customization);
        return acc;
      },
      {} as Record<string, ToolCustomization[]>,
    );

    return ImmutableMap(Object.entries(grouped).map(([toolId, customizations]) => [toolId, ImmutableList(customizations)]));
  }, [data]);

  return {
    ...query,
    toolCustomizations: data,
    toolCustomizationsMap,
  };
}

export function useSaveToolTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveToolTemplate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ToolTemplatesQuery.queryKey }),
  });
}

export function useDeleteToolTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteToolTemplate,
    onSuccess: (_data, id) => {
      queryClient.setQueryData<ToolCustomization[]>(ToolTemplatesQuery.queryKey, (prev) => prev?.filter((t) => t.id !== id));
      return queryClient.invalidateQueries({ queryKey: ToolTemplatesQuery.queryKey });
    },
  });
}
